import { useState } from "react";

export function PublishIntelModal({ bundle, filteredObjects, classification, onClose, onPublished, ui }) {
  const { B, fs } = ui;
  const [title, setTitle] = useState(bundle?.id || "Published STIX Intel");
  const [summary, setSummary] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const nonRels = (bundle?.objects || []).filter(o => o.type !== "relationship");
  const selectedIds = new Set((filteredObjects || []).map(o => o.id));
  const publishBundle = selectedOnly
    ? { ...bundle, objects: (bundle?.objects || []).filter(o => o.type !== "relationship" ? selectedIds.has(o.id) : selectedIds.has(o.source_ref) && selectedIds.has(o.target_ref)) }
    : bundle;
  const publishObjects = (publishBundle?.objects || []).filter(o => o.type !== "relationship");
  const tlpRed = String(classification || "").toUpperCase().includes("TLP:RED") || JSON.stringify(publishBundle || {}).toUpperCase().includes("TLP:RED");
  const tlpAmber = String(classification || "").toUpperCase().includes("TLP:AMBER") || JSON.stringify(publishBundle || {}).toUpperCase().includes("TLP:AMBER");

  const publish = async () => {
    if (!title.trim() || loading || tlpRed) return;
    setLoading(true);
    setStatus("");
    try {
      const res = await fetch("/api/intel/publish", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          summary: summary.trim(),
          tags: tagsText.split(",").map(t => t.trim()).filter(Boolean),
          classification,
          visibility,
          bundle: publishBundle,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Publish failed.");
      setStatus("Published.");
      onPublished?.(data.intel);
      setTimeout(onClose, 500);
    } catch (e) {
      setStatus(e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 16, padding: 30, width: 520, maxWidth: "92vw" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 800, fontSize: fs(17), color: B.text }}>Publish Intel</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(18) }}>x</button>
        </div>
        <div style={{ fontSize: fs(12), color: B.textDim, lineHeight: 1.5, marginBottom: 18 }}>Saved bundles stay private. This only publishes the objects you choose here.</div>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" style={{ width: "100%", marginBottom: 10, padding: "9px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", boxSizing: "border-box", outline: "none" }} />
        <textarea value={summary} onChange={e => setSummary(e.target.value)} placeholder="Short analyst summary..." rows={3} style={{ width: "100%", marginBottom: 10, padding: "9px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", resize: "vertical", boxSizing: "border-box", outline: "none" }} />
        <input value={tagsText} onChange={e => setTagsText(e.target.value)} placeholder="Tags, comma separated" style={{ width: "100%", marginBottom: 14, padding: "9px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", boxSizing: "border-box", outline: "none" }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[["public", "Public Feed"], ["unlisted", "Unlisted Link"]].map(([k, label]) => (
            <button key={k} onClick={() => setVisibility(k)} style={{ padding: "9px 10px", borderRadius: 8, border: `1px solid ${visibility === k ? B.primary : B.border}`, background: visibility === k ? B.primary + "18" : "transparent", color: visibility === k ? B.primary : B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: 700 }}>{label}</button>
          ))}
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, color: B.textMuted, fontSize: fs(12), cursor: "pointer", marginBottom: 12 }}>
          <input type="checkbox" checked={selectedOnly} onChange={e => setSelectedOnly(e.target.checked)} />
          Publish current filtered objects only <span style={{ color: B.textDim }}>({publishObjects.length} of {nonRels.length})</span>
        </label>
        {tlpRed && <div style={{ padding: 10, borderRadius: 8, background: "#ef444422", border: "1px solid #ef444455", color: "#fca5a5", fontSize: fs(12), marginBottom: 12 }}>TLP:RED intel cannot be published.</div>}
        {!tlpRed && tlpAmber && <div style={{ padding: 10, borderRadius: 8, background: "#f59e0b22", border: "1px solid #f59e0b55", color: "#fbbf24", fontSize: fs(12), marginBottom: 12 }}>This looks like TLP:AMBER. Double-check before publishing.</div>}
        <button onClick={publish} disabled={!title.trim() || loading || tlpRed || publishObjects.length === 0} style={{ width: "100%", padding: "10px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", color: B.white, cursor: !title.trim() || loading || tlpRed ? "not-allowed" : "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: 800, fontSize: fs(13), opacity: !title.trim() || loading || tlpRed ? 0.55 : 1 }}>
          {loading ? "Publishing..." : `Publish ${publishObjects.length} Object${publishObjects.length === 1 ? "" : "s"}`}
        </button>
        {status && <div style={{ marginTop: 10, fontSize: fs(12), color: status === "Published." ? "#16a34a" : "#ef4444" }}>{status}</div>}
      </div>
    </div>
  );
}

// The page itself is community/CommunityPage.js (Community Intel: Crowdsourced Reports, Submit, My OSINT).
export { CommunityIntelPage } from "./community/CommunityPage";
